import School from "pages/school-user";
import { Switch, Route, BrowserRouter as Router, Redirect } from "react-router-dom";
import Login from "pages/auth/login";
import SetPassword from "pages/auth/set-password";
import { AppURL } from "utils/Actions";
import Maintenance from "pages/auth/maintenance";
import { useEffect } from "react";
import { APP_STATUS, UI_ENVIRONMENT_KEY, WHITELABEL } from "utils/Constants";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { setMaintenance, clearMaintenance } from "redux/reducers/AppStatusSlice";
import { checkAppStatus } from "utils/hooks/checkAppStatus";
import { fetchProperties, fetchWhiteLabel } from "./redux/reducers/PropertiesSlice";
import TimeoutAlert from "./components/auth/TimeoutAlert";
import CreateAccount from "pages/auth/account-migration/create-account";
import i18n from "locales/i18n";
import ForgetPassword from "pages/auth/forget-password";
import AuthHelper from "components/auth/helpers";
import NotFound from "pages/school-user/not-found";
import GlobalLayout from "components/layout/global-layout";
import { Helmet } from "react-helmet";
import { Env } from "utils/Types";
import { isValidEnv, setEnv } from "redux/reducers/GlobalUiSlice";

const redirectInvalidUrl = (path: string) => {
  if (AuthHelper.isAuthed() && AuthHelper.isSchoolUser()) {
    return (
      <Route path={path}>
        <GlobalLayout>
          <NotFound />
        </GlobalLayout>
      </Route>
    );
  } else {
    return <Redirect to={"/"} />;
  }
};

const AppRouting = ({ appStatus }: any) => {
  switch (appStatus) {
    case APP_STATUS.MAINTENANCE:
      return (
        <Switch>
          <Route component={Maintenance} />
        </Switch>
      );
    default:
      return (
        <Switch>
          <Route path="/" exact component={Login} />
          <Route path={`${AppURL.createAccount}`} component={CreateAccount} />
          <Route path={`${AppURL.school.path}`} component={School} />
          <Route path={`${AppURL.setPassword}`} component={SetPassword} />
          <Route path={`${AppURL.forgetPassword}`} component={ForgetPassword} />
          <Route path="*" render={path => redirectInvalidUrl(path.match.url)} />
        </Switch>
      );
  }
};

const App: React.FC = () => {
  const dispatch = useAppDispatch();
  const appStatus = useAppSelector(state => state.appStatus.status);
  const whitelabel = useAppSelector(state => state.properties.whiteLabel);

  useEffect(() => {
    window.addEventListener("load", () => {
      console.log("Build version: " + process.env.REACT_APP_BUILD_VERSION);
      checkAppStatus(
        () => dispatch(setMaintenance()),
        () => dispatch(clearMaintenance())
      );
      dispatch(fetchProperties());
      dispatch(fetchWhiteLabel());
    });
  }, []);

  useEffect(() => {
    const institutionCode = whitelabel;
    const fileName = `en-x-${institutionCode}`;
    i18n.changeLanguage(fileName);
  }, [whitelabel]);

  useEffect(() => {
    if (whitelabel === WHITELABEL.uac) {
      // restore environment mode from localstorage
      const prevEnv = localStorage.getItem(UI_ENVIRONMENT_KEY) || Env.DOMESTIC;
      if (isValidEnv(prevEnv)) {
        dispatch(setEnv(prevEnv));
      } else {
        dispatch(setEnv(Env.DOMESTIC));
      }
    } else if (whitelabel === WHITELABEL.utas) {
      dispatch(setEnv(undefined));
    }
    // if whitelabel still loading, preserve previous env
  }, [whitelabel]);

  const faviconSrc = `${process.env.PUBLIC_URL}/favicon-${whitelabel}.ico`;

  return (
    <>
      <Helmet>
        <link rel="icon" href={faviconSrc} />
      </Helmet>
      <Router>
        <AppRouting appStatus={appStatus} />
        <TimeoutAlert />
      </Router>
    </>
  );
};

export default App;
